<template>
  <div class="space-y-6">
    <div class="bg-gradient-to-r from-red-500 to-orange-600 rounded-lg p-6 text-white">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold">Usuarios Bloqueados</h1>
          <p class="text-red-100 mt-1">Gestión de bloqueos entre usuarios</p>
        </div>
        <div class="hidden sm:flex space-x-4">
          <div class="bg-white/20 rounded-lg p-3 text-center">
            <p class="text-2xl font-bold">{{ stats.total }}</p>
            <p class="text-xs text-red-100">Total</p>
          </div>
          <div class="bg-white/20 rounded-lg p-3 text-center">
            <p class="text-2xl font-bold">{{ stats.recent }}</p>
            <p class="text-xs text-red-100">Últimos 7 días</p>
          </div>
        </div>
      </div>
    </div>

    <div class="card p-6">
      <div class="flex flex-col lg:flex-row gap-4 items-end">
        <div class="w-full lg:flex-1">
          <label class="block text-sm font-medium text-gray-700 mb-2">Buscar</label>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Buscar por nombre o email..."
            class="block w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
            @input="debouncedSearch"
          />
        </div>
      </div>
    </div>

    <div class="card">
      <DataTable
        :value="blocks"
        :loading="loading"
        paginator
        :rows="10"
        :rowsPerPageOptions="[5, 10, 20, 50]"
        :totalRecords="pagination.total"
        :lazy="true"
        @page="onPage"
        tableStyle="min-width: 50rem"
        class="p-datatable-sm"
      >
        <template #header>
          <div class="flex justify-between items-center">
            <h2 class="text-lg font-semibold text-gray-900">Lista de Bloqueos</h2>
          </div>
        </template>

        <template #empty>
          <div class="text-center py-12">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">No hay bloqueos</h3>
            <p class="mt-1 text-sm text-gray-500">No se encontraron bloqueos registrados.</p>
          </div>
        </template>

        <template #loading>
          <div class="text-center py-8">
            <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            <p class="mt-2 text-gray-600">Cargando bloqueos...</p>
          </div>
        </template>

        <Column header="Bloqueador">
          <template #body="{ data }">
            <div class="flex items-center">
              <div class="h-10 w-10 rounded-full bg-gradient-to-r from-red-500 to-orange-600 flex items-center justify-center text-white font-semibold mr-3">
                {{ data.blocker?.name?.charAt(0).toUpperCase() }}
              </div>
              <div>
                <p class="text-sm font-medium text-gray-900">{{ data.blocker?.name }}</p>
                <p class="text-sm text-gray-500">{{ data.blocker?.email }}</p>
              </div>
            </div>
          </template>
        </Column>

        <Column header="Bloqueado">
          <template #body="{ data }">
            <div class="flex items-center">
              <div class="h-10 w-10 rounded-full bg-gradient-to-r from-gray-500 to-gray-600 flex items-center justify-center text-white font-semibold mr-3">
                {{ data.blocked?.name?.charAt(0).toUpperCase() }}
              </div>
              <div>
                <p class="text-sm font-medium text-gray-900">{{ data.blocked?.name }}</p>
                <p class="text-sm text-gray-500">{{ data.blocked?.email }}</p>
              </div>
            </div>
          </template>
        </Column>

        <Column field="reason" header="Razón">
          <template #body="{ data }">
            <span class="text-sm text-gray-700">{{ data.reason || 'Sin razón especificada' }}</span>
          </template>
        </Column>

        <Column field="createdAt" header="Fecha" sortable>
          <template #body="{ data }">
            <span class="text-sm text-gray-900">{{ new Date(data.createdAt).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) }}</span>
          </template>
        </Column>

        <Column header="Acciones">
          <template #body="{ data }">
            <button
              @click="confirmDelete(data)"
              class="inline-flex items-center p-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all duration-200"
              title="Eliminar bloqueo"
            >
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </template>
        </Column>
      </DataTable>
    </div>

    <div v-if="showDeleteModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="showDeleteModal = false"></div>
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 class="text-lg leading-6 font-medium text-gray-900">Eliminar Bloqueo</h3>
                <div class="mt-2">
                  <p class="text-sm text-gray-500">
                    ¿Estás seguro de que quieres eliminar el bloqueo de <strong>{{ blockToDelete?.blocker?.name }}</strong> hacia <strong>{{ blockToDelete?.blocked?.name }}</strong>?
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button @click="deleteBlock" :disabled="deleting" class="btn btn-danger w-full sm:w-auto sm:ml-3">
              {{ deleting ? 'Eliminando...' : 'Eliminar' }}
            </button>
            <button @click="showDeleteModal = false" class="btn btn-secondary w-full sm:w-auto mt-3 sm:mt-0">
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import blockService from '@/services/blockService'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'

export default {
  name: 'Blocks',
  components: { DataTable, Column },
  setup() {
    const toast = useToast()
    const loading = ref(false)
    const deleting = ref(false)
    const blocks = ref([])
    const searchQuery = ref('')
    const showDeleteModal = ref(false)
    const blockToDelete = ref(null)
    const stats = reactive({ total: 0, recent: 0 })
    const pagination = reactive({ page: 1, limit: 10, total: 0, totalPages: 0 })
    let searchTimeout = null

    const loadBlocks = async () => {
      loading.value = true
      try {
        const response = await blockService.getBlocks({
          page: pagination.page,
          limit: pagination.limit,
          search: searchQuery.value
        })
        blocks.value = response.data.data
        Object.assign(pagination, response.data.pagination)
      } catch (error) {
        toast.error('Error al cargar bloqueos')
      } finally {
        loading.value = false
      }
    }

    const loadStats = async () => {
      try {
        const response = await blockService.getBlockStats()
        Object.assign(stats, response.data)
      } catch {}
    }

    const debouncedSearch = () => {
      clearTimeout(searchTimeout)
      searchTimeout = setTimeout(() => {
        pagination.page = 1
        loadBlocks()
      }, 300)
    }

    const onPage = (event) => {
      pagination.page = event.page + 1
      pagination.limit = event.rows
      loadBlocks()
    }

    const confirmDelete = (block) => {
      blockToDelete.value = block
      showDeleteModal.value = true
    }

    const deleteBlock = async () => {
      deleting.value = true
      try {
        await blockService.deleteBlock(blockToDelete.value.id)
        toast.success('Bloqueo eliminado correctamente')
        showDeleteModal.value = false
        blockToDelete.value = null
        loadBlocks()
        loadStats()
      } catch (error) {
        toast.error('Error al eliminar bloqueo')
      } finally {
        deleting.value = false
      }
    }

    onMounted(() => {
      loadBlocks()
      loadStats()
    })

    return {
      loading, deleting, blocks, searchQuery, showDeleteModal, blockToDelete,
      stats, pagination, debouncedSearch, onPage, confirmDelete, deleteBlock
    }
  }
}
</script>
